import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import {
  buildConditionPickerGroups,
  countConditionPickerItems,
  filterConditionPickerGroups,
} from "../utils/automationConditionCatalog";

const GROUP_ICONS = {
  solicitacao: AssignmentOutlinedIcon,
  cliente: PersonOutlineRoundedIcon,
  cliente_dados: PersonOutlineRoundedIcon,
  outros: MoreHorizRoundedIcon,
};

function SchemaFilterRow({ item, onSelect }) {
  return (
    <ListItemButton
      onClick={() => onSelect(item.key, item.def)}
      sx={{
        borderRadius: 1.5,
        mb: 0.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={600}>
            {item.title}
          </Typography>
        }
        secondary={item.description}
        secondaryTypographyProps={{ variant: "caption" }}
      />
    </ListItemButton>
  );
}

function ClientFieldGroupCard({ group, onSelectPreset }) {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {group.title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
        {group.description}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        {group.presets.map((preset) => {
          const filled = String(preset.value) === "1";
          const Icon = filled ? CheckCircleOutlineRoundedIcon : HighlightOffRoundedIcon;
          return (
            <Button
              key={`${preset.key}-${preset.value}`}
              variant="outlined"
              size="small"
              startIcon={<Icon fontSize="small" />}
              onClick={() => onSelectPreset(preset)}
              sx={{
                flex: 1,
                justifyContent: "flex-start",
                textAlign: "left",
                py: 1,
                borderColor: filled ? "success.light" : "warning.light",
                color: filled ? "success.dark" : "text.secondary",
                "&:hover": {
                  borderColor: filled ? "success.main" : "warning.main",
                  bgcolor: filled
                    ? alpha(theme.palette.success.main, 0.06)
                    : alpha(theme.palette.warning.main, 0.06),
                },
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  {preset.shortLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {filled ? "Campo preenchido" : "Campo ausente"}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function AutomationConditionPickerDialog({
  open,
  onClose,
  eventSchema = {},
  usedKeys = new Set(),
  hiddenKeys = new Set(),
  onSelectFilter,
  onSelectClientPreset,
}) {
  const [search, setSearch] = useState("");

  const allGroups = useMemo(
    () => buildConditionPickerGroups({ eventSchema, usedKeys, hiddenKeys }),
    [eventSchema, usedKeys, hiddenKeys]
  );

  const filteredGroups = useMemo(
    () => filterConditionPickerGroups(allGroups, search),
    [allGroups, search]
  );

  const totalItems = countConditionPickerItems(allGroups);
  const visibleItems = countConditionPickerItems(filteredGroups);

  const handleClose = () => {
    setSearch("");
    onClose?.();
  };

  const handleSelectFilter = (key, def) => {
    onSelectFilter?.(key, def);
    handleClose();
  };

  const handleSelectPreset = (preset) => {
    onSelectClientPreset?.(preset);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TuneRoundedIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="h6" component="span" fontWeight={700}>
              Adicionar filtro
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Limite quando a regra dispara. Depois você escolhe os valores de cada filtro.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por origem, status, tag, CPF, e-mail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {totalItems === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            Todos os filtros disponíveis já foram adicionados a esta regra.
          </Typography>
        ) : visibleItems === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            Nenhum filtro corresponde à busca &ldquo;{search}&rdquo;.
          </Typography>
        ) : (
          <Stack spacing={2.5} sx={{ maxHeight: "min(52vh, 440px)", overflowY: "auto", pr: 0.5 }}>
            {filteredGroups.map((group) => {
              const Icon = GROUP_ICONS[group.id] || TuneRoundedIcon;
              return (
                <Box key={group.id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Icon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {group.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.description}
                      </Typography>
                    </Box>
                  </Stack>

                  {group.schemaItems.length > 0 ? (
                    <List dense disablePadding sx={{ mb: group.clientGroups.length ? 1.5 : 0 }}>
                      {group.schemaItems.map((item) => (
                        <SchemaFilterRow
                          key={item.key}
                          item={item}
                          onSelect={handleSelectFilter}
                        />
                      ))}
                    </List>
                  ) : null}

                  {group.clientGroups.length > 0 ? (
                    <Stack spacing={1.25}>
                      {group.clientGroups.map((cg) => (
                        <ClientFieldGroupCard
                          key={cg.fieldKey}
                          group={cg}
                          onSelectPreset={handleSelectPreset}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Chip
          size="small"
          variant="outlined"
          label={
            search
              ? `${visibleItems} de ${totalItems} opções`
              : `${totalItems} opção${totalItems === 1 ? "" : "ões"} disponíveis`
          }
        />
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
